/*
  Interactive origin globe.

  Rendering is an inverse orthographic projection: every pixel inside the disc
  maps to exactly one lat/lon, which is looked up in a 2048x1024 land mask. A
  raster mask is used rather than coastline vectors because a vector globe must
  clip filled polygons against the visible hemisphere and close them along the
  limb -- the awkward part of the problem, and most of what a projection library
  is for. Sampling a mask has no clipping case at all. Measured at 3.5ms per
  frame for a 538x538 backing store, so a 2D canvas is sufficient and WebGL
  would add risk without buying anything.

  Figma paints the globe as ONE vector filled rgba(0,0,0,0.50) whose continents
  are holes in that path -- land is not white, it is absent, and the card fill
  shows through. That is why the mask decides alpha and never colour.
*/

if (!window.__hiraOriginGlobeInit) {
  window.__hiraOriginGlobeInit = true;

  const INSTANCES = new Map();

  const DEG = Math.PI / 180;
  const MASK_W = 2048;
  const MASK_H = 1024;

  /*
    The backing store is always 2x the CSS box rather than devicePixelRatio.
    Coastlines are hard-edged (there is no cheap way to antialias a mask
    lookup), so a 1x render on a non-retina screen looks chunky next to the
    Figma vector; 2x supersamples it away. Capping at 2 also bounds the cost on
    DPR-3 phones, where the browser's own downscale is imperceptible.
  */
  const RENDER_SCALE = 2;

  const HOME_SETTLE_MS = 1500;
  const DRAW_IN_MS = 900;
  const FRICTION = 0.94;
  const MIN_SPIN = 0.02;
  const EASE = 0.12;
  const SETTLED = 0.05;
  const KEY_STEP = 6;
  const PITCH_LIMIT = 78;
  const ARC_SAMPLES = 160;
  const MARKER_GAP = 12;

  const prefersReducedMotion = () =>
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const toVector = (lat, lon) => {
    const rl = lat * DEG;
    const rg = lon * DEG;
    const c = Math.cos(rl);
    return [c * Math.cos(rg), c * Math.sin(rg), Math.sin(rl)];
  };

  const toLatLon = ([x, y, z]) => {
    const n = Math.hypot(x, y, z) || 1;
    return [Math.asin(z / n) / DEG, Math.atan2(y, x) / DEG];
  };

  /* Shortest path along a great circle, so the arc is the real route. */
  const slerp = (a, b, t) => {
    const dot = Math.max(-1, Math.min(1, a[0] * b[0] + a[1] * b[1] + a[2] * b[2]));
    const omega = Math.acos(dot);
    if (omega < 1e-9) return a.slice();
    const s = Math.sin(omega);
    const wa = Math.sin((1 - t) * omega) / s;
    const wb = Math.sin(t * omega) / s;
    return [a[0] * wa + b[0] * wb, a[1] * wa + b[1] * wb, a[2] * wa + b[2] * wb];
  };

  /* Signed shortest angular difference, so easing never takes the long way. */
  const angleDelta = (from, to) => {
    let d = (to - from) % 360;
    if (d > 180) d -= 360;
    if (d < -180) d += 360;
    return d;
  };

  /*
    Returns null rather than a fallback pair. Substituting Grasse/Mumbai for an
    unusable value was worse than refusing: the marker keeps the merchant's own
    text, so the card would confidently label a dot that had been moved
    somewhere else. Range is checked as well as finiteness -- a longitude of 200
    projects to a plausible-looking position instead of failing where it can be
    seen.
  */
  const parsePair = (raw) => {
    const parts = String(raw || '').split(',');
    const lat = Number.parseFloat(parts[0]);
    const lon = Number.parseFloat(parts[1]);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return null;
    if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
    return [lat, lon];
  };

  /*
    The mask is fetched once per page and shared by every instance: the theme
    editor can re-render this block repeatedly, and re-decoding a 7KB PNG each
    time is pure waste. Bit-packed to 64KB rather than held as a 512KB byte
    array, which costs three operations per pixel against a measured budget
    that has room to spare.
  */
  let maskPromise = null;

  const fetchMask = (url) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      /*
        Theme assets are served with `access-control-allow-origin: *`, so the
        canvas stays untainted and getImageData is allowed. Without this the
        read throws a SecurityError on the live CDN while passing locally.
      */
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = MASK_W;
        canvas.height = MASK_H;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, MASK_W, MASK_H);
        let pixels;
        try {
          pixels = ctx.getImageData(0, 0, MASK_W, MASK_H).data;
        } catch (err) {
          reject(err);
          return;
        }
        const bits = new Uint8Array((MASK_W * MASK_H) >> 3);
        for (let i = 0; i < MASK_W * MASK_H; i++) {
          if (pixels[i << 2] > 127) bits[i >> 3] |= 1 << (i & 7);
        }
        resolve(bits);
      };
      img.onerror = () => reject(new Error('mask failed to load'));
      img.src = url;
    });

  const loadMask = (url) => {
    if (maskPromise) return maskPromise;
    maskPromise = fetchMask(url).catch((err) => {
      /*
        Cleared on failure so a later instance can try again. Caching the
        rejection pinned the flat-disc fallback for the whole life of the page
        after one transient network error.
      */
      maskPromise = null;
      throw err;
    });
    return maskPromise;
  };

  const create = (root) => {
    const canvas = root.querySelector('[data-hira-globe-canvas]');
    if (!canvas) return null;

    const ctx = canvas.getContext('2d');
    const markers = [...root.querySelectorAll('[data-hira-globe-focus]')];
    const controller = new AbortController();
    const { signal } = controller;

    const from = parsePair(root.dataset.globeFrom);
    const to = parsePair(root.dataset.globeTo);
    if (!from || !to) {
      /*
        Hidden rather than left as a bare disc. Both city names are already
        carried by the markers and again by the ORIGIN / FINISHED tiles, so
        dropping the illustration costs a reader nothing.
      */
      root.hidden = true;
      console.warn(
        '[hira-globe] Unusable coordinates, globe hidden:',
        root.dataset.globeFrom,
        root.dataset.globeTo
      );
      return null;
    }

    const fromVec = toVector(from[0], from[1]);
    const toVec = toVector(to[0], to[1]);

    /*
      At-rest view is the great-circle midpoint of the two cities rather than a
      hand-picked centre: it is the only rotation guaranteed to show both
      endpoints and the whole arc, whatever cities the merchant sets.
    */
    const home = toLatLon(slerp(fromVec, toVec, 0.5));

    const cities = [
      { key: 'from', vec: fromVec, latLon: from },
      { key: 'to', vec: toVec, latLon: to },
    ];

    let mask = null;
    let raf = 0;
    let width = 0;
    let height = 0;
    let radius = 0;
    let image = null;
    let alpha = null;
    let sphereX = null;
    let sphereY = null;
    let sphereD = null;
    let coverage = null;
    let inside = null;

    let pitch = home[0];
    let yaw = home[1];
    let targetPitch = home[0];
    let targetYaw = home[1];
    let spin = 0;
    let tilt = 0;

    let dragging = false;
    let pointerId = null;
    let lastX = 0;
    let lastY = 0;
    let settleAt = 0;
    let holding = false;
    let drawInStart = 0;
    let revealed = prefersReducedMotion();
    let dirty = true;

    const styles = getComputedStyle(root);
    const accent = styles.getPropertyValue('--hira-globe-accent').trim() || '#436067';
    const discAlpha =
      Number.parseFloat(styles.getPropertyValue('--hira-globe-disc-alpha')) || 0.5;
    const arcWidth =
      Number.parseFloat(styles.getPropertyValue('--hira-globe-arc-width')) || 1.5;
    const dotRadius =
      Number.parseFloat(styles.getPropertyValue('--hira-globe-dot-size')) / 2 || 4.5;

    const discByte = Math.round(Math.max(0, Math.min(1, discAlpha)) * 255);

    /*
      Per-pixel unit sphere vectors, coverage and the in-disc flag depend only
      on the canvas size, never on rotation -- so they are built once per resize
      and the render loop is left with nine multiplies and two trig calls per
      pixel. That is what turns this from ~25ms a frame into ~3.5ms.
    */
    const buildPixelTables = () => {
      const n = width * height;
      image = ctx.createImageData(width, height);
      alpha = image.data;
      sphereX = new Float32Array(n);
      sphereY = new Float32Array(n);
      sphereD = new Float32Array(n);
      coverage = new Uint8Array(n);
      inside = new Uint8Array(n);

      const cx = width / 2;
      const cy = height / 2;
      radius = Math.min(cx, cy);

      for (let j = 0; j < height; j++) {
        const y = (cy - j - 0.5) / radius;
        for (let i = 0; i < width; i++) {
          const k = j * width + i;
          const x = (i + 0.5 - cx) / radius;
          const r2 = x * x + y * y;
          if (r2 > 1) continue;
          inside[k] = 1;
          sphereX[k] = x;
          sphereY[k] = y;
          sphereD[k] = Math.sqrt(1 - r2);
          /*
            One pixel of feathering at the limb. The mask lookup cannot be
            antialiased, but the disc edge is the only boundary against the card
            and a hard circle there reads as a rendering bug.
          */
          const edge = (1 - Math.sqrt(r2)) * radius;
          coverage[k] = Math.round(Math.max(0, Math.min(1, edge)) * discByte);
        }
      }
    };

    const resize = () => {
      const box = canvas.getBoundingClientRect();
      const w = Math.max(1, Math.round(box.width * RENDER_SCALE));
      const h = Math.max(1, Math.round(box.height * RENDER_SCALE));
      if (w === width && h === height) return;
      width = w;
      height = h;
      canvas.width = width;
      canvas.height = height;
      buildPixelTables();
      dirty = true;
      schedule();
    };

    /* World -> screen. Returns null when the point is on the far hemisphere. */
    const project = (vec, sinP, cosP, sinY, cosY) => {
      const [vx, vy, vz] = vec;
      const meridian = vx * cosY + vy * sinY;
      const depth = cosP * meridian + vz * sinP;
      if (depth < 0) return null;
      const x = -vx * sinY + vy * cosY;
      const y = -sinP * meridian + vz * cosP;
      return [width / 2 + x * radius, height / 2 - y * radius];
    };

    const paintSphere = (sinP, cosP, sinY, cosY) => {
      /*
        Inverse of the projection above, written out as a 3x3 so the inner loop
        has no trig of its own beyond the two lookups it cannot avoid.
      */
      const m00 = cosY * cosP;
      const m01 = -sinY;
      const m02 = -cosY * sinP;
      const m10 = sinY * cosP;
      const m11 = cosY;
      const m12 = -sinY * sinP;
      const m20 = sinP;
      const m22 = cosP;

      const lonScale = MASK_W / (2 * Math.PI);
      const latScale = MASK_H / Math.PI;
      const n = width * height;

      for (let k = 0; k < n; k++) {
        const o = (k << 2) + 3;
        if (!inside[k]) {
          alpha[o] = 0;
          continue;
        }
        const x = sphereX[k];
        const y = sphereY[k];
        const d = sphereD[k];
        const vx = m00 * d + m01 * x + m02 * y;
        const vy = m10 * d + m11 * x + m12 * y;
        const vz = m20 * d + m22 * y;
        const u = ((Math.atan2(vy, vx) + Math.PI) * lonScale) | 0;
        const w = ((Math.PI / 2 - Math.asin(vz)) * latScale) | 0;
        const idx = (w < 0 ? 0 : w >= MASK_H ? MASK_H - 1 : w) * MASK_W +
          (u < 0 ? 0 : u >= MASK_W ? MASK_W - 1 : u);
        /* Land is a hole in Figma's path, so land means fully transparent. */
        alpha[o] = (mask[idx >> 3] >> (idx & 7)) & 1 ? 0 : coverage[k];
      }
      ctx.putImageData(image, 0, 0);
    };

    const paintRoute = (sinP, cosP, sinY, cosY, progress) => {
      const scale = RENDER_SCALE;
      ctx.strokeStyle = accent;
      ctx.fillStyle = accent;
      ctx.lineWidth = arcWidth * scale;
      ctx.lineCap = 'round';
      ctx.setLineDash([4 * scale, 3 * scale]);

      const upTo = Math.max(1, Math.round(ARC_SAMPLES * progress));
      ctx.beginPath();
      let pen = false;
      for (let i = 0; i <= upTo; i++) {
        const point = project(slerp(fromVec, toVec, i / ARC_SAMPLES), sinP, cosP, sinY, cosY);
        if (!point) {
          pen = false;
          continue;
        }
        if (pen) ctx.lineTo(point[0], point[1]);
        else ctx.moveTo(point[0], point[1]);
        pen = true;
      }
      ctx.stroke();
      ctx.setLineDash([]);

      for (const city of cities) {
        const point = project(city.vec, sinP, cosP, sinY, cosY);
        if (!point) continue;
        ctx.beginPath();
        ctx.arc(point[0], point[1], dotRadius * scale, 0, Math.PI * 2);
        ctx.fill();
      }
    };

    const step = (now) => {
      raf = 0;
      if (!mask) return;

      /*
        `moving` means the view changed and must be repainted; `awaiting` only
        keeps the loop alive. They were one flag, which meant the 1.5s pause
        before drifting home repainted the whole canvas ~90 times without
        anything moving.
      */
      let moving = false;
      let awaiting = false;

      if (!dragging) {
        if (Math.abs(spin) > MIN_SPIN || Math.abs(tilt) > MIN_SPIN) {
          yaw += spin;
          pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch + tilt));
          spin *= FRICTION;
          tilt *= FRICTION;
          settleAt = now + HOME_SETTLE_MS;
          moving = true;
        } else {
          spin = 0;
          tilt = 0;
          /*
            Drag drifts back to the target view once the user stops, so the card
            can never be left with its own subject spun out of sight. A label
            tap sets `holding`, which parks the globe there deliberately.
          */
          if (holding || now >= settleAt) {
            const dy = angleDelta(yaw, targetYaw);
            const dp = targetPitch - pitch;
            if (Math.abs(dy) > SETTLED || Math.abs(dp) > SETTLED) {
              if (prefersReducedMotion()) {
                yaw = targetYaw;
                pitch = targetPitch;
                /*
                  `dirty` rather than `moving`: the jump needs one repaint but
                  no follow-up frame. Without it the rotation was updated and
                  never drawn, so a reduced-motion drag never appeared to
                  return home.
                */
                dirty = true;
              } else {
                yaw += dy * EASE;
                pitch += dp * EASE;
                moving = true;
              }
            }
          } else {
            awaiting = true;
          }
        }
      }

      let progress = 1;
      if (!revealed) {
        if (!drawInStart) drawInStart = now;
        const t = Math.min(1, (now - drawInStart) / DRAW_IN_MS);
        /* easeOutCubic -- the route should arrive, not coast to a stop. */
        progress = 1 - (1 - t) ** 3;
        if (t >= 1) revealed = true;
        else moving = true;
      }

      if (moving || dirty) {
        const rp = pitch * DEG;
        const ry = yaw * DEG;
        const sinP = Math.sin(rp);
        const cosP = Math.cos(rp);
        const sinY = Math.sin(ry);
        const cosY = Math.cos(ry);
        paintSphere(sinP, cosP, sinY, cosY);
        paintRoute(sinP, cosP, sinY, cosY, progress);
        dirty = false;
      }

      if (moving || awaiting) schedule();
    };

    const schedule = () => {
      if (!raf && mask) raf = requestAnimationFrame(step);
    };

    const onPointerDown = (event) => {
      if (!mask || event.button > 0) return;
      dragging = true;
      holding = false;
      pointerId = event.pointerId;
      lastX = event.clientX;
      lastY = event.clientY;
      spin = 0;
      tilt = 0;
      canvas.setPointerCapture(pointerId);
      canvas.classList.add('is-grabbing');
    };

    const onPointerMove = (event) => {
      if (!dragging || event.pointerId !== pointerId) return;
      const dx = event.clientX - lastX;
      const dy = event.clientY - lastY;
      lastX = event.clientX;
      lastY = event.clientY;
      /*
        Turntable drag, not versor: a full CSS-pixel diameter of travel is 180
        degrees. Versor would keep the grabbed point exactly under the cursor,
        but `touch-action: pan-y` already restricts touch to one axis, so the
        extra quaternion machinery would buy nothing a user could feel.
      */
      const perPixel = 90 / (radius / RENDER_SCALE);
      spin = -dx * perPixel;
      tilt = dy * perPixel;
      yaw += spin;
      pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch + tilt));
      dirty = true;
      schedule();
    };

    const endDrag = (event) => {
      if (!dragging || (event && event.pointerId !== pointerId)) return;
      dragging = false;
      if (pointerId !== null && canvas.hasPointerCapture(pointerId)) {
        canvas.releasePointerCapture(pointerId);
      }
      pointerId = null;
      canvas.classList.remove('is-grabbing');
      if (prefersReducedMotion()) {
        spin = 0;
        tilt = 0;
      }
      settleAt = performance.now() + HOME_SETTLE_MS;
      schedule();
    };

    const onKeyDown = (event) => {
      const map = {
        ArrowLeft: [KEY_STEP, 0],
        ArrowRight: [-KEY_STEP, 0],
        ArrowUp: [0, -KEY_STEP],
        ArrowDown: [0, KEY_STEP],
      };
      const move = map[event.key];
      if (!move) return;
      event.preventDefault();
      holding = false;
      yaw += move[0];
      pitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, pitch + move[1]));
      settleAt = performance.now() + HOME_SETTLE_MS;
      dirty = true;
      schedule();
    };

    /*
      WCAG 2.2 SC 2.5.7 requires a single-pointer alternative to any dragging
      movement, and W3C names map panning as an example. These buttons are that
      alternative -- and they double as the keyboard path under 2.1.1. A second
      press returns to the at-rest view so the control is a toggle, never a
      one-way trip.
    */
    const onMarkerClick = (event) => {
      const key = event.currentTarget.dataset.hiraGlobeFocus;
      const city = cities.find((c) => c.key === key);
      if (!city) return;
      const focused = holding &&
        Math.abs(angleDelta(targetYaw, city.latLon[1])) < 1 &&
        Math.abs(targetPitch - city.latLon[0]) < 1;
      if (focused) {
        targetPitch = home[0];
        targetYaw = home[1];
        holding = false;
      } else {
        targetPitch = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, city.latLon[0]));
        targetYaw = city.latLon[1];
        holding = true;
      }
      spin = 0;
      tilt = 0;
      schedule();
    };

    canvas.addEventListener('pointerdown', onPointerDown, { signal });
    canvas.addEventListener('pointermove', onPointerMove, { signal });
    canvas.addEventListener('pointerup', endDrag, { signal });
    canvas.addEventListener('pointercancel', endDrag, { signal });
    canvas.addEventListener('keydown', onKeyDown, { signal });
    for (const marker of markers) {
      marker.addEventListener('click', onMarkerClick, { signal });
    }

    /*
      Created here but deliberately not observing yet: `resize()` builds ~5.2MB
      of typed arrays and runs a 289k-iteration loop, and observing immediately
      pulled all of it forward to first layout for a card that is typically
      2400px below the fold. Only the fetch was behind the reveal gate; now the
      setup is too.
    */
    const resizeObserver = new ResizeObserver(resize);

    /*
      Nothing is fetched or painted until the card is close to the viewport --
      this block sits far below the fold on a PDP that is being tuned for PSI.
    */
    const reveal = new IntersectionObserver(
      (entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        reveal.disconnect();
        loadMask(root.dataset.globeMask)
          .then((bits) => {
            mask = bits;
            root.classList.add('is-ready');
            resize();
            resizeObserver.observe(canvas);
            dirty = true;
            schedule();
          })
          .catch(() => {
            /* Leaves the static fallback in place rather than an empty box. */
            root.classList.add('is-unavailable');
          });
      },
      { rootMargin: '200px' }
    );
    reveal.observe(root);

    return {
      destroy: () => {
        controller.abort();
        resizeObserver.disconnect();
        reveal.disconnect();
        if (raf) cancelAnimationFrame(raf);
        raf = 0;
      },
    };
  };

  const initOne = (el) => {
    if (el.hasAttribute('data-hira-globe-inited')) return;
    const instance = create(el);
    if (!instance) return;
    INSTANCES.set(el, instance);
    el.setAttribute('data-hira-globe-inited', 'true');
  };

  const destroyOne = (el) => {
    INSTANCES.get(el)?.destroy();
    INSTANCES.delete(el);
    el.removeAttribute?.('data-hira-globe-inited');
  };

  const scanWithin = (root) => {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    for (const el of root.querySelectorAll('[data-hira-globe]:not([data-hira-globe-inited])')) {
      initOne(el);
    }
    if (root.matches?.('[data-hira-globe]:not([data-hira-globe-inited])')) initOne(root);
  };

  const destroyWithin = (root) => {
    if (!root || typeof root.querySelectorAll !== 'function') return;
    for (const el of root.querySelectorAll('[data-hira-globe-inited]')) destroyOne(el);
  };

  const scanAll = () => scanWithin(document);

  document.addEventListener('DOMContentLoaded', scanAll);
  document.addEventListener('shopify:section:load', (e) => scanWithin(e.target));
  document.addEventListener('shopify:section:unload', (e) => destroyWithin(e.target));

  if (document.readyState !== 'loading') scanAll();
}
